/**
 * SEARCH-002 §3.2 / §A5 — le CORPS VISUEL d'une ligne de résultat de recherche.
 *
 * SOURCE UNIQUE partagée par la palette ⌘K (`HitItem`) et le sélecteur « Relier
 * une fiche » d'une tâche (`LinkedEntityPicker`) — comme `searchGrouping` l'est
 * déjà pour l'ORDRE des groupes. Les deux surfaces affichent la même réponse
 * serveur : elles doivent la donner à lire de la même façon.
 *
 * Ce que la ligne porte, et pourquoi :
 * - **avatar ou initiales** pour les types « personne » (contact, apprenant,
 *   collègue, intervenant) — un visage se reconnaît plus vite qu'un libellé ;
 * - **l'icône de catégorie seulement en tête** (`leadingIcon`), jamais répétée
 *   sur chaque ligne : le domaine se lit sur l'en-tête de groupe (§2.2) ;
 * - **les mots cherchés surlignés** (`highlightMatch`), dans le titre et le
 *   sous-titre — sans eux, sur une liste d'homonymes, rien ne dit POURQUOI cette
 *   ligne est là ;
 * - **le sous-titre humanisé** (`humanizeMeta`) — jamais un code enum brut ;
 * - **la pastille du champ trouvé** quand la correspondance ne vient PAS du
 *   libellé (e-mail, SIRET, référence…) : c'est la seule chose qui explique une
 *   ligne dont le nom ne ressemble pas à la saisie.
 *
 * Composant purement présentationnel : il ne rend PAS le `CommandItem`, pour que
 * chaque surface garde sa propre sémantique de sélection (naviguer d'un côté,
 * choisir de l'autre).
 */

import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    CATEGORY_REGISTRY,
    humanizeMeta,
    initialsFromLabel,
    isPersonType,
    type SearchHit,
} from '@/lib/searchCategories'
import { highlightMatch } from './highlightMatch'

export function SearchHitRow({
    hit,
    query,
    leadingIcon = false,
}: {
    hit: SearchHit
    query: string
    /** Icône de catégorie en tête de ligne (« meilleur résultat », listes sans groupe). */
    leadingIcon?: boolean
}) {
    const { t } = useTranslation()
    const Icon = CATEGORY_REGISTRY[hit.type].icon
    const person = isPersonType(hit.type)
    const showBadge = hit.matchedField && hit.matchedField !== 'label' && hit.matchedField !== 'name'

    return (
        <>
            {hit.avatarUrl ? (
                <Avatar className="h-7 w-7">
                    <AvatarImage src={hit.avatarUrl} alt="" />
                    <AvatarFallback className="text-[11px] font-semibold text-muted-foreground">
                        {person ? (
                            initialsFromLabel(hit.label)
                        ) : (
                            <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                        )}
                    </AvatarFallback>
                </Avatar>
            ) : person ? (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-muted-foreground">
                    {initialsFromLabel(hit.label)}
                </span>
            ) : leadingIcon ? (
                <Icon className="text-muted-foreground" aria-hidden="true" />
            ) : null}
            {/* Titre ET sous-titre sur 1 ligne ellipsis chacun (liste dense, §3.2). */}
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{highlightMatch(hit.label, query)}</span>
                {hit.secondary && (
                    <span className="truncate text-xs text-muted-foreground">
                        {highlightMatch(humanizeMeta(hit.secondary, t), query)}
                    </span>
                )}
            </div>
            {showBadge && (
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {t(`globalSearch.matched.${hit.matchedField}`, hit.matchedField!)}
                </span>
            )}
        </>
    )
}
